import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tagRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tagList: {
    maxHeight: 360,
    overflowY: "auto",
    marginTop: theme.spacing(1),
  },
  selectedPreview: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

const normalizeSelectedTags = (data) => {
  if (!data) return [];
  if (Array.isArray(data.tags) && data.tags.length > 0) {
    return data.tags.filter((tag) => tag && tag.id);
  }
  if (data.tag && data.tag.id) {
    return [data.tag];
  }
  return [];
};

const FlowBuilderTagCheckModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (open === "edit" || open === "create") {
      (async () => {
        try {
          const { data: result } = await api.get("/tags/list");
          if (!isMounted.current) return;

          const availableTags = Array.isArray(result) ? result : [];
          setTags(availableTags);

          if (open === "edit") {
            const current = normalizeSelectedTags(data?.data);
            setSelectedIds(current.map((tag) => tag.id));
          } else {
            setSelectedIds([]);
          }

          setActiveModal(true);
        } catch (error) {
          console.log(error);
          toast.error("Erro ao carregar tags");
        }
      })();
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const toggleTag = (tagId) => {
    setSelectedIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      return toast.error("Selecione pelo menos uma tag");
    }

    const selectedTags = tags.filter((tag) => selectedIds.includes(tag.id));

    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          ...data.data,
          tags: selectedTags,
          tag: selectedTags[0] || null,
        },
      });
    } else if (open === "create") {
      onSave({
        tags: selectedTags,
        tag: selectedTags[0] || null,
      });
    }

    handleClose();
  };

  const selectedTagsPreview = tags.filter((tag) => selectedIds.includes(tag.id));

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="tag-check-dialog-title">
          {open === "create" ? "Verificar tags no contato" : "Editar verificação de tags"}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" paragraph>
              Selecione uma ou mais tags. Se o contato possuir{" "}
              <strong>qualquer uma</strong> delas, o fluxo segue pela saída{" "}
              <strong>TEM</strong>. Caso contrário, segue pela saída{" "}
              <strong>NÃO TEM</strong>.
            </Typography>

            {selectedTagsPreview.length > 0 && (
              <Box className={classes.selectedPreview}>
                {selectedTagsPreview.map((tag) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    onDelete={() => toggleTag(tag.id)}
                    style={{
                      backgroundColor: tag.color ? `${tag.color}22` : undefined,
                      borderColor: tag.color || "#ccc",
                    }}
                  />
                ))}
              </Box>
            )}

            <Typography variant="subtitle2">Tags disponíveis</Typography>
            <div className={classes.tagList}>
              {tags.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhuma tag cadastrada.
                </Typography>
              ) : (
                tags.map((tag) => (
                  <div key={tag.id} className={classes.tagRow}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIds.includes(tag.id)}
                          onChange={() => toggleTag(tag.id)}
                          color="primary"
                        />
                      }
                      label={
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: tag.color || "#9e9e9e",
                              display: "inline-block",
                            }}
                          />
                          {tag.name}
                        </span>
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              className={classes.btnWrapper}
              onClick={handleSave}
            >
              {open === "create" ? "Adicionar" : "Salvar"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderTagCheckModal;
